import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
interface WeeklySalesData {
  kw: number;
  jahr: number;
  erstgesprächeVereinbart: number;
  erstgesprächeErschienen: number;
  qualifiziert: number;
  nichtQualifiziert: number;
  verkaufsgesprächeVereinbart: number;
  verkaufsgesprächeErschienen: number;
  abschlüsse: number;
  umsatz: number;
}
interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  field: keyof WeeklySalesData;
  weeklyData: WeeklySalesData[];
  onSave: (kw: number, value: number) => void;
}
export function EditModal({
  isOpen,
  onClose,
  title,
  field,
  weeklyData,
  onSave
}: EditModalProps) {
  const {
    theme
  } = useTheme();
  const [selectedKw, setSelectedKw] = useState<number>(weeklyData[0]?.kw || 1);
  const [editValue, setEditValue] = useState<string>('');
  if (!isOpen) return null;
  const selectedWeek = weeklyData.find(w => w.kw === selectedKw);
  const currentValue = selectedWeek ? selectedWeek[field] : 0;
  const handleSave = () => {
    const numValue = parseFloat(editValue);
    if (!isNaN(numValue)) {
      onSave(selectedKw, numValue);
    }
    onClose();
  };
  const handleKwChange = (kw: number) => {
    setSelectedKw(kw);
    const week = weeklyData.find(w => w.kw === kw);
    if (week) {
      setEditValue(week[field].toString());
    }
  };
  // Initialize edit value when modal opens
  useEffect(() => {
    if (isOpen && selectedWeek) {
      setEditValue(selectedWeek[field].toString());
    }
  }, [isOpen, selectedWeek, field]);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-6">
      <div className={`w-full max-w-md rounded-xl shadow-2xl ${theme === 'dark' ? 'bg-[#1f1f1f] border border-[#2a2a2a]' : 'bg-white border border-gray-200'}`} data-id="element-7">
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${theme === 'dark' ? 'border-[#2a2a2a]' : 'border-gray-200'}`} data-id="element-8">
          <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-black'}`} data-id="element-9">
            {title} bearbeiten
          </h2>
          <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${theme === 'dark' ? 'hover:bg-[#2a2a2a] text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-black'}`} data-id="element-10">
            <X className="w-5 h-5" data-id="element-11" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-12">
          {/* Week Selector */}
          <div data-id="element-13">
            <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-14">
              Kalenderwoche auswählen
            </label>
            <select value={selectedKw} onChange={e => handleKwChange(Number(e.target.value))} className={`w-full px-4 py-2 rounded-lg font-medium focus:outline-none focus:ring-2 ${theme === 'dark' ? 'bg-[#2a2a2a] text-white border border-[#3a3a3a] focus:border-[#FF7A59] focus:ring-[#FF7A59]/20' : 'bg-gray-50 text-black border border-gray-300 focus:border-[#FF7A59] focus:ring-[#FF7A59]/20'}`} data-id="element-15">
              {weeklyData.map(week => <option key={week.kw} value={week.kw} data-id="element-16">
                  KW {week.kw} - {week.jahr}
                </option>)}
            </select>
          </div>
          {/* Current Value Display */}
          <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-[#2a2a2a] border border-[#3a3a3a]' : 'bg-gray-50 border border-gray-200'}`} data-id="element-17">
            <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-18">
              Aktueller Wert
            </p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-black'}`} data-id="element-19">
              {currentValue}
            </p>
          </div>
          {/* Value Input */}
          <div data-id="element-20">
            <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-21">
              Neuer Wert
            </label>
            <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)} className={`w-full px-4 py-3 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 ${theme === 'dark' ? 'bg-[#2a2a2a] text-white border border-[#3a3a3a] focus:border-[#FF7A59] focus:ring-[#FF7A59]/20' : 'bg-gray-50 text-black border border-gray-300 focus:border-[#FF7A59] focus:ring-[#FF7A59]/20'}`} placeholder="Wert eingeben" autoFocus data-id="element-22" />
          </div>
          {/* Info Text */}
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`} data-id="element-23">
            Die Gesamtsumme wird automatisch nach dem Speichern aktualisiert.
          </p>
        </div>
        {/* Footer */}
        <div className={`flex items-center justify-end gap-3 p-6 border-t ${theme === 'dark' ? 'border-[#2a2a2a]' : 'border-gray-200'}`} data-id="element-24">
          <button onClick={onClose} className={`px-4 py-2 rounded-lg font-medium transition-colors ${theme === 'dark' ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a]' : 'bg-gray-100 text-black hover:bg-gray-200'}`} data-id="element-25">
            Abbrechen
          </button>
          <button onClick={handleSave} className="px-4 py-2 rounded-lg font-medium bg-[#FF7A59] text-white hover:bg-[#FF6B4A] transition-colors" data-id="element-26">
            Speichern
          </button>
        </div>
      </div>
    </div>;
}